import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { useSalon } from "../context/SalonContext";
import { subscriptionsApi } from "../lib/subscriptionsApi";

const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDate = (s) => {
    if (!s) return "—";
    try {
        const d = typeof s === "string" ? new Date(s + (s.length === 10 ? "T00:00:00" : "")) : new Date(s);
        return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return s; }
};
const fmtMonth = (period) => {
    if (!period) return "";
    const [y, m] = period.split("-");
    const months = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
    return `${months[parseInt(m, 10) - 1]}/${y.slice(2)}`;
};

const CYCLE_LABEL = { monthly: "Mensal", quarterly: "Trimestral", semiannual: "Semestral", annual: "Anual" };
const CYCLE_OPTIONS = ["monthly", "quarterly", "semiannual", "annual"];

const STATUS_META = {
    active:    { label: "Ativo",     chip: "bg-emerald-500/15 text-emerald-200 border-emerald-500/40", dot: "bg-emerald-400" },
    expiring:  { label: "Vencendo",  chip: "bg-amber-500/15   text-amber-200   border-amber-500/40",   dot: "bg-amber-400" },
    expired:   { label: "Vencido",   chip: "bg-red-500/15     text-red-200     border-red-500/40",     dot: "bg-red-400" },
    cancelled: { label: "Cancelado", chip: "bg-slate-500/15   text-slate-300   border-slate-500/30",   dot: "bg-slate-400" },
};

// ═══ ROOT: hub with 3 cards ══════════════════════════════════════════════
const Subscriptions = () => {
    const [view, setView] = useState("hub"); // hub | plans | subscribers | revenue
    const [summary, setSummary] = useState(null);
    const [refreshTick, setRefreshTick] = useState(0);

    const loadSummary = useCallback(async () => {
        try { setSummary(await subscriptionsApi.summary()); } catch (e) { toast.error("Falha ao carregar KPIs", { description: e.message }); }
    }, []);
    useEffect(() => { loadSummary(); }, [loadSummary, refreshTick]);

    const bump = () => setRefreshTick((t) => t + 1);

    if (view !== "hub") {
        return (
            <div data-testid="subscriptions-module" className="space-y-6">
                <button
                    data-testid="subs-back-btn"
                    onClick={() => { setView("hub"); bump(); }}
                    className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
                >
                    <i className="fa-solid fa-arrow-left"></i>Voltar
                </button>
                {view === "plans" && <PlansSection onChange={bump} />}
                {view === "subscribers" && <SubscribersSection onChange={bump} />}
                {view === "revenue" && <RevenueSection summary={summary} />}
            </div>
        );
    }

    return (
        <div data-testid="subscriptions-module" className="space-y-8">
            <SubscriptionsKPIs summary={summary} />
            <HubCards summary={summary} onOpen={setView} />
        </div>
    );
};

// ═══ Hub KPIs (top) ═══════════════════════════════════════════════════════
const SubscriptionsKPIs = ({ summary }) => {
    const items = [
        { icon: "fa-users", label: "Assinantes ativos", value: summary?.active_subscribers ?? "—", tone: "emerald" },
        { icon: "fa-repeat", label: "MRR do Clube", value: summary ? brl(summary.mrr) : "—", tone: "amber" },
        { icon: "fa-arrow-rotate-right", label: "Renovações este mês", value: summary?.renewals_this_month ?? "—", tone: "sky" },
        { icon: "fa-triangle-exclamation", label: "Vencendo em 7 dias", value: summary?.expiring_soon ?? "—", tone: summary?.expiring_soon > 0 ? "warning" : "slate" },
    ];
    const tones = {
        emerald: "from-emerald-500/20 to-emerald-500/5 border-emerald-500/25",
        amber:   "from-amber-500/20 to-amber-500/5 border-amber-500/25",
        sky:     "from-sky-500/20 to-sky-500/5 border-sky-500/25",
        warning: "from-amber-500/25 to-red-500/10 border-amber-500/40",
        slate:   "from-slate-500/10 to-slate-500/5 border-white/10",
    };
    return (
        <div data-testid="subs-kpis" className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {items.map((it) => (
                <div key={it.label} data-testid={`subs-kpi-${it.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                     className={`rounded-2xl bg-gradient-to-br ${tones[it.tone]} border p-5 min-h-[120px]`}>
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-slate-400">
                        <i className={`fa-solid ${it.icon}`}></i><span>{it.label}</span>
                    </div>
                    <div className="mt-3 salon-heading text-3xl text-white">{it.value}</div>
                </div>
            ))}
        </div>
    );
};

// ═══ Hub 3 cards ═════════════════════════════════════════════════════════
const HubCards = ({ summary, onOpen }) => {
    const cards = [
        { id: "plans",       icon: "fa-clipboard-list", title: "Planos",     subtitle: "Configure seus planos mensais", stat: `${summary?.plans_count ?? ""}`, cta: "Gerenciar planos" },
        { id: "subscribers", icon: "fa-users",          title: "Assinantes", subtitle: "Vincule clientes aos planos",   stat: summary ? `${summary.active_subscribers} ativos` : "", cta: "Ver assinantes" },
        { id: "revenue",     icon: "fa-sack-dollar",    title: "Receita",    subtitle: "MRR e histórico mês a mês",     stat: summary ? brl(summary.mrr) + "/mês" : "", cta: "Ver receita" },
    ];
    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {cards.map((c) => (
                <button
                    key={c.id}
                    data-testid={`subs-hub-card-${c.id}`}
                    onClick={() => onOpen(c.id)}
                    className="group text-left rounded-3xl p-6 lg:p-8 border border-white/10 bg-gradient-to-br from-slate-900/60 to-slate-950/40 hover:border-amber-500/40 hover:from-amber-500/5 transition-all duration-300 min-h-[220px] flex flex-col justify-between"
                >
                    <div className="flex items-start justify-between">
                        <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-amber-500/20 to-amber-500/5 border border-amber-500/30 flex items-center justify-center text-amber-300 text-2xl">
                            <i className={`fa-solid ${c.icon}`}></i>
                        </div>
                        <i className="fa-solid fa-arrow-up-right text-slate-600 group-hover:text-amber-400 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"></i>
                    </div>
                    <div className="mt-6">
                        <h3 className="salon-heading text-2xl text-white">{c.title}</h3>
                        <p className="text-sm text-slate-400 mt-1">{c.subtitle}</p>
                        {c.stat && <div className="mt-4 text-xs uppercase tracking-[0.25em] text-amber-300 font-semibold">{c.stat}</div>}
                    </div>
                </button>
            ))}
        </div>
    );
};

// ═══ SECTION 1: PLANS ═════════════════════════════════════════════════════
const PlansSection = ({ onChange }) => {
    const { services } = useSalon();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try { setPlans(await subscriptionsApi.listPlans()); }
        catch (e) { toast.error("Falha ao carregar planos", { description: e.message }); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const handleToggle = async (plan) => {
        try {
            await subscriptionsApi.updatePlan(plan.id, { active: !plan.active });
            toast.success(plan.active ? "Plano desativado" : "Plano reativado");
            await load(); onChange && onChange();
        } catch (e) { toast.error("Falha ao alterar", { description: e.message }); }
    };

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Clube de Assinatura</div>
                    <h2 className="salon-heading text-2xl text-white mt-1">Planos</h2>
                </div>
                <button
                    data-testid="subs-plans-new-btn"
                    onClick={() => { setEditing(null); setShowModal(true); }}
                    className="h-11 px-5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-xs uppercase tracking-[0.2em] rounded-xl transition-all"
                >
                    <i className="fa-solid fa-plus mr-2"></i>Novo Plano
                </button>
            </div>

            {loading && <div className="text-slate-500 text-sm">Carregando...</div>}
            {!loading && plans.length === 0 && (
                <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center text-slate-500">
                    <i className="fa-solid fa-clipboard-list text-4xl mb-3 block text-slate-700"></i>
                    Nenhum plano criado ainda. Clique em <strong className="text-slate-300">Novo Plano</strong> para começar.
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {plans.map((p) => {
                    const svcNames = services.filter((s) => (p.service_ids || []).includes(s.id)).map((s) => s.name);
                    return (
                        <div key={p.id} data-testid={`subs-plan-${p.id}`}
                             className={`rounded-2xl p-5 border transition-all ${p.active ? "border-white/10 bg-slate-900/40" : "border-white/5 bg-slate-950/40 opacity-70"}`}>
                            <div className="flex items-start justify-between">
                                <div>
                                    <h3 className="salon-heading text-xl text-white">{p.name}</h3>
                                    {p.description && <p className="text-xs text-slate-400 mt-0.5">{p.description}</p>}
                                </div>
                                {!p.active && (
                                    <span className="text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full bg-slate-500/15 text-slate-400 border border-slate-500/30">Inativo</span>
                                )}
                            </div>
                            <div className="mt-4 flex items-baseline gap-1">
                                <span className="salon-heading text-3xl text-amber-300">{brl(p.price)}</span>
                                <span className="text-xs text-slate-500">/{CYCLE_LABEL[p.billing_cycle] || "mensal"}</span>
                            </div>
                            <div className="mt-4 space-y-2">
                                <div className="flex items-center gap-2 text-xs text-slate-400">
                                    <i className="fa-solid fa-check text-emerald-400"></i>
                                    {p.uses_per_month === 0 ? "Usos ilimitados" : `${p.uses_per_month} usos/mês`}
                                </div>
                                <div className="flex items-center gap-2 text-xs text-slate-400">
                                    <i className="fa-solid fa-user text-sky-400"></i>
                                    {p.active_subscribers || 0} assinante{p.active_subscribers === 1 ? "" : "s"}
                                </div>
                            </div>
                            {svcNames.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-1.5">
                                    {svcNames.slice(0, 4).map((n) => (
                                        <span key={n} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-300 border border-sky-500/20">{n}</span>
                                    ))}
                                    {svcNames.length > 4 && (
                                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 text-slate-400">+{svcNames.length - 4}</span>
                                    )}
                                </div>
                            )}
                            <div className="mt-4 pt-4 border-t border-white/5 flex gap-2">
                                <button
                                    data-testid={`subs-plan-edit-${p.id}`}
                                    onClick={() => { setEditing(p); setShowModal(true); }}
                                    className="flex-1 h-9 px-3 rounded-lg border border-white/10 hover:border-white/25 text-xs text-slate-300"
                                >
                                    <i className="fa-solid fa-pen mr-1.5"></i>Editar
                                </button>
                                <button
                                    data-testid={`subs-plan-toggle-${p.id}`}
                                    onClick={() => handleToggle(p)}
                                    className={`h-9 px-3 rounded-lg text-xs border ${p.active ? "border-red-500/30 hover:bg-red-500/10 text-red-300" : "border-emerald-500/30 hover:bg-emerald-500/10 text-emerald-300"}`}
                                >
                                    <i className={`fa-solid ${p.active ? "fa-pause" : "fa-play"} mr-1.5`}></i>
                                    {p.active ? "Desativar" : "Ativar"}
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {showModal && (
                <PlanModal
                    plan={editing}
                    services={services}
                    onClose={() => setShowModal(false)}
                    onSaved={async () => { setShowModal(false); await load(); onChange && onChange(); }}
                />
            )}
        </div>
    );
};

const PlanModal = ({ plan, services, onClose, onSaved }) => {
    const [form, setForm] = useState({
        name: plan?.name || "",
        description: plan?.description || "",
        price: plan?.price ?? "",
        service_ids: plan?.service_ids || [],
        uses_per_month: plan?.uses_per_month ?? 4,
        unlimited: plan?.uses_per_month === 0,
        billing_cycle: plan?.billing_cycle || "monthly",
        active: plan?.active !== false,
    });
    const [saving, setSaving] = useState(false);

    const toggleService = (id) => {
        setForm((f) => ({
            ...f,
            service_ids: f.service_ids.includes(id) ? f.service_ids.filter((x) => x !== id) : [...f.service_ids, id],
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) return toast.error("Informe o nome do plano");
        if (!form.price || parseFloat(form.price) <= 0) return toast.error("Preço inválido");
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                description: form.description.trim(),
                price: parseFloat(form.price),
                service_ids: form.service_ids,
                uses_per_month: form.unlimited ? 0 : parseInt(form.uses_per_month, 10) || 0,
                billing_cycle: form.billing_cycle,
                active: form.active,
            };
            if (plan) await subscriptionsApi.updatePlan(plan.id, payload);
            else await subscriptionsApi.createPlan(payload);
            toast.success(plan ? "Plano atualizado" : "Plano criado!");
            onSaved();
        } catch (err) {
            toast.error("Falha ao salvar", { description: err.message });
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <form
                data-testid="subs-plan-modal"
                onClick={(e) => e.stopPropagation()}
                onSubmit={handleSubmit}
                className="w-full max-w-2xl rounded-3xl bg-slate-900 border border-white/10 p-6 max-h-[90vh] overflow-y-auto"
            >
                <div className="flex items-center justify-between mb-5">
                    <h3 className="salon-heading text-xl text-white">{plan ? "Editar Plano" : "Novo Plano"}</h3>
                    <button type="button" onClick={onClose} className="text-slate-500 hover:text-white"><i className="fa-solid fa-xmark"></i></button>
                </div>

                <div className="space-y-4">
                    <Field label="Nome do plano *">
                        <input data-testid="subs-plan-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                               className="w-full bg-slate-950/60 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white" />
                    </Field>
                    <Field label="Descrição">
                        <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                               placeholder="Ex: 2 cortes por mês"
                               className="w-full bg-slate-950/60 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white" />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Preço *">
                            <input data-testid="subs-plan-price" type="number" step="0.01" value={form.price}
                                   onChange={(e) => setForm({ ...form, price: e.target.value })}
                                   className="w-full bg-slate-950/60 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white" />
                        </Field>
                        <Field label="Ciclo de cobrança">
                            <div className="flex flex-wrap gap-1">
                                {CYCLE_OPTIONS.map((c) => (
                                    <button type="button" key={c} onClick={() => setForm({ ...form, billing_cycle: c })}
                                            className={`text-[10px] uppercase tracking-wider px-2.5 py-2 rounded-lg border transition-all ${form.billing_cycle === c ? "bg-amber-500 text-slate-950 border-amber-400" : "border-white/10 text-slate-400 hover:text-white"}`}>
                                        {CYCLE_LABEL[c]}
                                    </button>
                                ))}
                            </div>
                        </Field>
                    </div>
                    <Field label="Usos por mês">
                        <div className="flex items-center gap-3">
                            <input type="number" min="0" value={form.uses_per_month}
                                   disabled={form.unlimited}
                                   onChange={(e) => setForm({ ...form, uses_per_month: e.target.value })}
                                   className="flex-1 bg-slate-950/60 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white disabled:opacity-40" />
                            <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                                <input type="checkbox" checked={form.unlimited} onChange={(e) => setForm({ ...form, unlimited: e.target.checked })} />
                                Ilimitado
                            </label>
                        </div>
                    </Field>
                    <Field label={`Serviços incluídos (${form.service_ids.length})`}>
                        <div className="max-h-40 overflow-y-auto rounded-xl border border-white/10 bg-slate-950/40 p-2 space-y-1">
                            {services.length === 0 && <div className="text-xs text-slate-500 p-2">Cadastre serviços no Catálogo antes.</div>}
                            {services.map((s) => {
                                const on = form.service_ids.includes(s.id);
                                return (
                                    <label key={s.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-colors ${on ? "bg-emerald-500/10" : "hover:bg-white/5"}`}>
                                        <input type="checkbox" checked={on} onChange={() => toggleService(s.id)} />
                                        <span className="text-sm text-slate-200 flex-1">{s.name}</span>
                                        <span className="text-xs text-slate-500">{brl(s.price)}</span>
                                    </label>
                                );
                            })}
                        </div>
                    </Field>
                    <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                        <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                        Plano ativo (aceita novos assinantes)
                    </label>
                </div>

                <div className="mt-6 flex gap-2 justify-end">
                    <button type="button" onClick={onClose} className="h-10 px-4 rounded-xl border border-white/10 text-slate-400 text-xs uppercase tracking-[0.2em]">Cancelar</button>
                    <button data-testid="subs-plan-save" type="submit" disabled={saving} className="h-10 px-5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-xs uppercase tracking-[0.2em] rounded-xl disabled:opacity-50">
                        {saving ? "Salvando..." : plan ? "Salvar" : "Criar plano"}
                    </button>
                </div>
            </form>
        </div>
    );
};

const Field = ({ label, children }) => (
    <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">{label}</div>
        {children}
    </div>
);

// ═══ SECTION 2: SUBSCRIBERS ═══════════════════════════════════════════════
const SubscribersSection = ({ onChange }) => {
    const { clients } = useSalon();
    const [subs, setSubs] = useState([]);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [showLink, setShowLink] = useState(false);
    const [detail, setDetail] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, p] = await Promise.all([subscriptionsApi.list(), subscriptionsApi.listPlans()]);
            setSubs(s); setPlans(p);
        } catch (e) { toast.error("Falha ao carregar", { description: e.message }); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        if (filter === "all") return subs;
        return subs.filter((s) => s.status === filter);
    }, [subs, filter]);

    const counters = useMemo(() => {
        const c = { all: subs.length, active: 0, expiring: 0, expired: 0, cancelled: 0 };
        subs.forEach((s) => { c[s.status] = (c[s.status] || 0) + 1; });
        return c;
    }, [subs]);

    const handleAction = async (sub, action) => {
        try {
            if (action === "renew") {
                await subscriptionsApi.renew(sub.id, { payment_method: sub.payment_method || "pix" });
                toast.success("Assinatura renovada!");
            } else if (action === "cancel") {
                const reason = window.prompt("Motivo do cancelamento (opcional):", "");
                if (reason === null) return;
                await subscriptionsApi.cancel(sub.id, { reason });
                toast.success("Assinatura cancelada");
            }
            await load(); onChange && onChange();
        } catch (e) { toast.error("Falha", { description: e.message }); }
    };

    const filters = [
        { id: "all", label: "Todos" },
        { id: "active", label: "Ativos" },
        { id: "expiring", label: "Vencendo" },
        { id: "expired", label: "Vencidos" },
        { id: "cancelled", label: "Cancelados" },
    ];

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Clube de Assinatura</div>
                    <h2 className="salon-heading text-2xl text-white mt-1">Assinantes</h2>
                </div>
                <button
                    data-testid="subs-link-btn"
                    onClick={() => setShowLink(true)}
                    disabled={plans.filter((p) => p.active).length === 0}
                    className="h-11 px-5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-xs uppercase tracking-[0.2em] rounded-xl disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <i className="fa-solid fa-user-plus mr-2"></i>Vincular Cliente
                </button>
            </div>

            {/* Filter pills */}
            <div className="flex items-center gap-1 p-1 bg-slate-900/40 rounded-xl border border-white/5 w-fit overflow-x-auto">
                {filters.map((f) => {
                    const active = filter === f.id;
                    return (
                        <button key={f.id} data-testid={`subs-filter-${f.id}`}
                                onClick={() => setFilter(f.id)}
                                className={`px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition-all whitespace-nowrap ${active ? "bg-white text-slate-900" : "text-slate-400 hover:text-white"}`}>
                            {f.label} <span className={active ? "text-slate-500 ml-1" : "text-slate-600 ml-1"}>({counters[f.id] || 0})</span>
                        </button>
                    );
                })}
            </div>

            {/* Table */}
            <div className="rounded-2xl border border-white/5 bg-slate-900/30 overflow-hidden">
                {loading && <div className="p-6 text-sm text-slate-500">Carregando...</div>}
                {!loading && filtered.length === 0 && (
                    <div className="p-10 text-center text-slate-500 text-sm">
                        <i className="fa-solid fa-inbox text-4xl mb-3 block text-slate-700"></i>
                        Nenhuma assinatura {filter !== "all" && `com status "${filter}"`}.
                    </div>
                )}
                {!loading && filtered.length > 0 && (
                    <table data-testid="subs-table" className="w-full text-sm">
                        <thead className="bg-slate-950/60 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            <tr>
                                <th className="text-left py-3 px-4">Cliente</th>
                                <th className="text-left py-3 px-4">Plano</th>
                                <th className="text-left py-3 px-4">Uso mês</th>
                                <th className="text-left py-3 px-4">Renovação</th>
                                <th className="text-left py-3 px-4">Status</th>
                                <th className="text-right py-3 px-4">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {filtered.map((s) => {
                                const meta = STATUS_META[s.status] || STATUS_META.active;
                                const limit = s.plan?.uses_per_month || 0;
                                const used = s.uses_this_month || 0;
                                const pct = limit === 0 ? 100 : Math.min(100, (used / limit) * 100);
                                return (
                                    <tr key={s.id} data-testid={`subs-row-${s.id}`} className="hover:bg-white/[0.02]">
                                        <td className="py-3 px-4">
                                            <div className="text-white text-sm font-medium">{s.client?.name || "—"}</div>
                                            <div className="text-[11px] text-slate-500">{s.client?.phone || ""}</div>
                                        </td>
                                        <td className="py-3 px-4">
                                            <div className="text-slate-200 text-sm">{s.plan?.name || "—"}</div>
                                            <div className="text-[11px] text-amber-300">{brl(s.plan?.price || 0)}</div>
                                        </td>
                                        <td className="py-3 px-4">
                                            <div className="text-xs text-slate-400 mb-1">
                                                {limit === 0 ? `${used} usos` : `${used} de ${limit}`}
                                            </div>
                                            <div className="h-1.5 w-24 bg-white/5 rounded-full overflow-hidden">
                                                <div className="h-full bg-gradient-to-r from-amber-500 to-amber-400" style={{ width: `${pct}%` }}></div>
                                            </div>
                                        </td>
                                        <td className="py-3 px-4 text-slate-300 text-sm">{fmtDate(s.next_renewal)}</td>
                                        <td className="py-3 px-4">
                                            <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full border ${meta.chip}`}>
                                                <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`}></span>
                                                {meta.label}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4">
                                            <div className="flex items-center gap-1 justify-end">
                                                <button data-testid={`subs-view-${s.id}`} onClick={() => setDetail(s.id)}
                                                        title="Ver detalhes" className="h-8 w-8 rounded-lg border border-white/10 hover:border-white/25 text-slate-400 hover:text-white">
                                                    <i className="fa-solid fa-eye"></i>
                                                </button>
                                                {s.status !== "cancelled" && (
                                                    <>
                                                        <button data-testid={`subs-renew-${s.id}`} onClick={() => handleAction(s, "renew")}
                                                                title="Renovar" className="h-8 w-8 rounded-lg border border-emerald-500/30 hover:bg-emerald-500/10 text-emerald-300">
                                                            <i className="fa-solid fa-rotate"></i>
                                                        </button>
                                                        <button data-testid={`subs-cancel-${s.id}`} onClick={() => handleAction(s, "cancel")}
                                                                title="Cancelar" className="h-8 w-8 rounded-lg border border-red-500/30 hover:bg-red-500/10 text-red-300">
                                                            <i className="fa-solid fa-xmark"></i>
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {showLink && (
                <LinkClientModal
                    clients={clients}
                    plans={plans.filter((p) => p.active)}
                    subs={subs}
                    onClose={() => setShowLink(false)}
                    onSaved={async () => { setShowLink(false); await load(); onChange && onChange(); }}
                />
            )}
            {detail && <SubscriptionDetailDrawer subId={detail} onClose={() => setDetail(null)} onChange={load} />}
        </div>
    );
};

const LinkClientModal = ({ clients, plans, subs, onClose, onSaved }) => {
    const [search, setSearch] = useState("");
    const [clientId, setClientId] = useState("");
    const [planId, setPlanId] = useState(plans[0]?.id || "");
    const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
    const [payment, setPayment] = useState("pix");
    const [saving, setSaving] = useState(false);

    const busyClientIds = useMemo(
        () => new Set(subs.filter((s) => s.status !== "cancelled" && s.status !== "expired").map((s) => s.client_id)),
        [subs]
    );

    const filteredClients = useMemo(() => {
        const q = search.trim().toLowerCase();
        return clients.filter((c) => {
            if (busyClientIds.has(c.id)) return false;
            if (!q) return true;
            return (c.name || "").toLowerCase().includes(q) || (c.phone || "").includes(q);
        }).slice(0, 20);
    }, [clients, search, busyClientIds]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!clientId) return toast.error("Selecione um cliente");
        if (!planId) return toast.error("Selecione um plano");
        setSaving(true);
        try {
            await subscriptionsApi.create({
                client_id: clientId, plan_id: planId, start_date: startDate,
                payment_method: payment, register_payment: true,
            });
            toast.success("Assinatura criada!", { description: "Cliente vinculado com sucesso." });
            onSaved();
        } catch (err) { toast.error("Falha", { description: err.message }); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <form
                data-testid="subs-link-modal"
                onClick={(e) => e.stopPropagation()}
                onSubmit={handleSubmit}
                className="w-full max-w-lg rounded-3xl bg-slate-900 border border-white/10 p-6 max-h-[90vh] overflow-y-auto"
            >
                <div className="flex items-center justify-between mb-5">
                    <h3 className="salon-heading text-xl text-white">Vincular Cliente ao Clube</h3>
                    <button type="button" onClick={onClose} className="text-slate-500 hover:text-white"><i className="fa-solid fa-xmark"></i></button>
                </div>
                <div className="space-y-4">
                    <Field label="Buscar cliente">
                        <input data-testid="subs-link-search" value={search} onChange={(e) => setSearch(e.target.value)}
                               placeholder="Nome ou telefone..."
                               className="w-full bg-slate-950/60 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white" />
                    </Field>
                    <div className="max-h-48 overflow-y-auto rounded-xl border border-white/10 bg-slate-950/40 p-2 space-y-1">
                        {filteredClients.length === 0 && <div className="text-xs text-slate-500 p-2">Nenhum cliente disponível.</div>}
                        {filteredClients.map((c) => (
                            <label key={c.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer ${clientId === c.id ? "bg-amber-500/10 border border-amber-500/30" : "hover:bg-white/5"}`}>
                                <input type="radio" name="client" checked={clientId === c.id} onChange={() => setClientId(c.id)} />
                                <div className="flex-1">
                                    <div className="text-sm text-white">{c.name}</div>
                                    <div className="text-[11px] text-slate-500">{c.phone}</div>
                                </div>
                            </label>
                        ))}
                    </div>
                    <Field label="Plano">
                        <select data-testid="subs-link-plan" value={planId} onChange={(e) => setPlanId(e.target.value)}
                                className="w-full bg-slate-950/60 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white">
                            {plans.map((p) => (<option key={p.id} value={p.id}>{p.name} — {brl(p.price)}</option>))}
                        </select>
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Data de início">
                            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                                   className="w-full bg-slate-950/60 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white" />
                        </Field>
                        <Field label="Forma de pagamento">
                            <select value={payment} onChange={(e) => setPayment(e.target.value)}
                                    className="w-full bg-slate-950/60 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white">
                                <option value="pix">PIX</option>
                                <option value="cash">Dinheiro</option>
                                <option value="card">Cartão</option>
                            </select>
                        </Field>
                    </div>
                </div>
                <div className="mt-6 flex gap-2 justify-end">
                    <button type="button" onClick={onClose} className="h-10 px-4 rounded-xl border border-white/10 text-slate-400 text-xs uppercase tracking-[0.2em]">Cancelar</button>
                    <button data-testid="subs-link-confirm" type="submit" disabled={saving || !clientId || !planId}
                            className="h-10 px-5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-xs uppercase tracking-[0.2em] rounded-xl disabled:opacity-50">
                        {saving ? "Vinculando..." : "Confirmar assinatura"}
                    </button>
                </div>
            </form>
        </div>
    );
};

const SubscriptionDetailDrawer = ({ subId, onClose, onChange }) => {
    const [sub, setSub] = useState(null);
    useEffect(() => {
        subscriptionsApi.get(subId).then(setSub).catch(() => toast.error("Falha ao carregar"));
    }, [subId]);
    if (!sub) return null;
    const meta = STATUS_META[sub.status] || STATUS_META.active;
    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-stretch justify-end" onClick={onClose}>
            <div data-testid="subs-detail-drawer" onClick={(e) => e.stopPropagation()}
                 className="w-full max-w-lg bg-slate-900 border-l border-white/10 overflow-y-auto p-6">
                <div className="flex items-center justify-between mb-5">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Assinatura</div>
                        <h3 className="salon-heading text-xl text-white mt-1">{sub.client?.name}</h3>
                    </div>
                    <button onClick={onClose} className="text-slate-500 hover:text-white"><i className="fa-solid fa-xmark text-xl"></i></button>
                </div>

                <div className="space-y-4">
                    <div className="rounded-2xl border border-white/10 bg-slate-950/40 p-4">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-sm text-white font-medium">{sub.plan?.name}</div>
                            <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full border ${meta.chip}`}>
                                <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`}></span>{meta.label}
                            </span>
                        </div>
                        <div className="text-2xl text-amber-300 salon-heading">{brl(sub.plan?.price || 0)}</div>
                        <div className="grid grid-cols-2 gap-3 mt-3 text-xs">
                            <div>
                                <div className="text-slate-500 uppercase tracking-wider text-[10px]">Início</div>
                                <div className="text-slate-200">{fmtDate(sub.start_date)}</div>
                            </div>
                            <div>
                                <div className="text-slate-500 uppercase tracking-wider text-[10px]">Renovação</div>
                                <div className="text-slate-200">{fmtDate(sub.next_renewal)}</div>
                            </div>
                            <div>
                                <div className="text-slate-500 uppercase tracking-wider text-[10px]">Uso mensal</div>
                                <div className="text-slate-200">{sub.uses_this_month}/{sub.plan?.uses_per_month === 0 ? "∞" : sub.plan?.uses_per_month}</div>
                            </div>
                            <div>
                                <div className="text-slate-500 uppercase tracking-wider text-[10px]">Pagamento</div>
                                <div className="text-slate-200 uppercase">{sub.payment_method}</div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-2">Pagamentos ({(sub.payments || []).length})</div>
                        <div className="space-y-1">
                            {(sub.payments || []).slice(0, 8).map((p) => (
                                <div key={p.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/[0.02] border border-white/5">
                                    <div className="text-sm text-slate-200">{brl(p.amount)}</div>
                                    <div className="text-xs text-slate-500">{fmtDate(p.paid_at)} · {p.payment_method}</div>
                                </div>
                            ))}
                            {(sub.payments || []).length === 0 && <div className="text-xs text-slate-500 text-center py-3">Sem pagamentos</div>}
                        </div>
                    </div>

                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-2">Usos ({(sub.uses || []).length})</div>
                        <div className="space-y-1">
                            {(sub.uses || []).slice(0, 8).map((u) => (
                                <div key={u.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/[0.02] border border-white/5">
                                    <div className="text-sm text-slate-200">Uso em comanda</div>
                                    <div className="text-xs text-slate-500">{fmtDate(u.used_at)}</div>
                                </div>
                            ))}
                            {(sub.uses || []).length === 0 && <div className="text-xs text-slate-500 text-center py-3">Nenhum uso registrado</div>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ═══ SECTION 3: REVENUE ═══════════════════════════════════════════════════
const RevenueSection = ({ summary: initialSummary }) => {
    const [summary, setSummary] = useState(initialSummary);
    const [history, setHistory] = useState([]);
    const [pays, setPays] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const [s, h, subs] = await Promise.all([
                    subscriptionsApi.summary(),
                    subscriptionsApi.history(),
                    subscriptionsApi.list(),
                ]);
                setSummary(s); setHistory(h);
                // Junta payments direto do subs enriquecido — pega apenas do mês atual para tabela.
                const paysArr = [];
                for (const sub of subs) {
                    const detail = await subscriptionsApi.get(sub.id).catch(() => null);
                    if (detail?.payments) {
                        for (const p of detail.payments) {
                            paysArr.push({ ...p, client_name: sub.client?.name, plan_name: sub.plan?.name });
                        }
                    }
                }
                paysArr.sort((a, b) => new Date(b.paid_at) - new Date(a.paid_at));
                setPays(paysArr.slice(0, 50));
            } catch (e) { toast.error("Falha ao carregar receita", { description: e.message }); }
        })();
    }, []);

    const handleExport = async () => {
        try {
            const blob = await subscriptionsApi.exportCsvDownload();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `kinng-assinaturas-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            toast.success("CSV exportado");
        } catch (e) { toast.error("Falha ao exportar", { description: e.message }); }
    };

    const chartData = useMemo(() => history.map((h) => ({ ...h, label: fmtMonth(h.period) })), [history]);
    const variation = summary?.variation_pct ?? 0;

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Clube de Assinatura</div>
                    <h2 className="salon-heading text-2xl text-white mt-1">Receita</h2>
                </div>
                <button data-testid="subs-export-btn" onClick={handleExport}
                        className="h-10 px-4 rounded-xl border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 text-amber-200 text-xs uppercase tracking-[0.2em]">
                    <i className="fa-solid fa-file-csv mr-2"></i>Exportar CSV
                </button>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <RevenueKPI label="MRR do Clube" value={summary ? brl(summary.mrr) : "—"} icon="fa-repeat" tone="amber" />
                <RevenueKPI label="Receita do mês" value={summary ? brl(summary.revenue_current) : "—"} icon="fa-calendar-day" tone="emerald" />
                <RevenueKPI label="Mês anterior" value={summary ? brl(summary.revenue_previous) : "—"} icon="fa-clock-rotate-left" tone="sky" />
                <RevenueKPI label="Variação" value={summary ? `${variation > 0 ? "+" : ""}${variation.toFixed(1)}%` : "—"}
                            icon={variation >= 0 ? "fa-arrow-trend-up" : "fa-arrow-trend-down"} tone={variation >= 0 ? "emerald" : "red"} />
            </div>

            {/* Chart */}
            <div className="rounded-2xl border border-white/5 bg-slate-900/30 p-5">
                <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">Receita mês a mês (últimos 6 meses)</div>
                <div className="h-64" data-testid="subs-revenue-chart">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="grad-gold" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.5} />
                                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                            <XAxis dataKey="label" stroke="#64748b" style={{ fontSize: 11 }} />
                            <YAxis stroke="#64748b" style={{ fontSize: 11 }} tickFormatter={(v) => `R$${v}`} />
                            <Tooltip
                                contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                                formatter={(v) => brl(v)}
                            />
                            <Area type="monotone" dataKey="revenue" stroke="#f59e0b" strokeWidth={2} fill="url(#grad-gold)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Payments table */}
            <div className="rounded-2xl border border-white/5 bg-slate-900/30 overflow-hidden">
                <div className="px-5 py-4 border-b border-white/5">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Pagamentos recentes</div>
                    <div className="text-sm text-slate-300 mt-0.5">{pays.length} pagamento{pays.length !== 1 ? "s" : ""}</div>
                </div>
                {pays.length === 0 ? (
                    <div className="p-10 text-center text-slate-500 text-sm">
                        <i className="fa-solid fa-money-bill text-4xl mb-3 block text-slate-700"></i>
                        Nenhum pagamento registrado ainda.
                    </div>
                ) : (
                    <table className="w-full text-sm" data-testid="subs-payments-table">
                        <thead className="bg-slate-950/60 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            <tr>
                                <th className="text-left py-3 px-4">Cliente</th>
                                <th className="text-left py-3 px-4">Plano</th>
                                <th className="text-right py-3 px-4">Valor</th>
                                <th className="text-left py-3 px-4">Data</th>
                                <th className="text-left py-3 px-4">Forma</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {pays.map((p) => (
                                <tr key={p.id} className="hover:bg-white/[0.02]">
                                    <td className="py-3 px-4 text-white">{p.client_name || "—"}</td>
                                    <td className="py-3 px-4 text-slate-300">{p.plan_name || "—"}</td>
                                    <td className="py-3 px-4 text-right text-amber-300 font-semibold">{brl(p.amount)}</td>
                                    <td className="py-3 px-4 text-slate-400">{fmtDate(p.paid_at)}</td>
                                    <td className="py-3 px-4 text-slate-400 uppercase text-xs">{p.payment_method}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

const RevenueKPI = ({ label, value, icon, tone }) => {
    const tones = {
        amber:   "from-amber-500/20 to-amber-500/5 border-amber-500/25 text-amber-200",
        emerald: "from-emerald-500/20 to-emerald-500/5 border-emerald-500/25 text-emerald-200",
        sky:     "from-sky-500/20 to-sky-500/5 border-sky-500/25 text-sky-200",
        red:     "from-red-500/20 to-red-500/5 border-red-500/25 text-red-200",
    };
    return (
        <div className={`rounded-2xl bg-gradient-to-br ${tones[tone]} border p-4 min-h-[104px]`}>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-slate-400">
                <i className={`fa-solid ${icon}`}></i><span>{label}</span>
            </div>
            <div className="mt-2 salon-heading text-2xl text-white">{value}</div>
        </div>
    );
};

export default Subscriptions;
